import type { Question } from '@shared/domain/form';
import type { BaseReview } from '@shared/domain/review';
import type { ReviewFormConfig } from '../hooks/useReviewForm';
import React from 'react';
import { CenteredDiv } from '@shared/styles/FormStyles';
import { getReviewStepName } from '../helpers/ReviewStepNames';
import { useReviewForm } from '../hooks/useReviewForm';
import { Container, SaveBar, SaveButton } from '../styles/review.styles';
import { QuestionRenderer } from './QuestionRenderer';

interface BaseReviewFormProps<T extends BaseReview> extends ReviewFormConfig<T> {
  containerTestId?: string;
  submitButtonText?: string;
  formFields: Question[];
  disabledOptionsGetter?: (questionName: string) => string[] | undefined;
  headerSection?: React.ReactNode;
  footerSection?: React.ReactNode;
  customQuestionRenderer?: React.ReactNode;
  hideDefaultButtons?: boolean;
}

export function BaseReviewForm<T extends BaseReview>({
  containerTestId,
  submitButtonText = 'Continue',
  formFields: questionFields,
  disabledOptionsGetter,
  headerSection,
  footerSection,
  customQuestionRenderer,
  hideDefaultButtons = false,
  reviewStep,
  ...config
}: BaseReviewFormProps<T>) {
  const {
    formFields,
    extendFormFields,
    setFormFields,
    errors,
    validateField,
    handleFormFieldChange,
    handleSubmit,
    handleEdit,
  } = useReviewForm<T>({ ...config, reviewStep, formFields: questionFields });

  return (
    <Container style={{ paddingBottom: '2rem' }}>
      {containerTestId && <div data-testid={containerTestId} />}

      {headerSection}

      {customQuestionRenderer || (
        <QuestionRenderer
          questions={questionFields}
          formFields={formFields}
          errors={errors}
          handleFormFieldChange={handleFormFieldChange}
          extendFormFields={extendFormFields}
          validateField={validateField}
          setFormFields={setFormFields}
          disabledOptionsGetter={disabledOptionsGetter}
        />
      )}

      {footerSection}

      {!hideDefaultButtons && (config.editMode
        ? (
            <SaveBar>
              <SaveButton onClick={handleEdit}>{`Save ${getReviewStepName(reviewStep)} Changes`}</SaveButton>
            </SaveBar>
          )
        : (
            <CenteredDiv>
              <SaveButton onClick={handleSubmit}>{submitButtonText}</SaveButton>
            </CenteredDiv>
          ))}
    </Container>
  );
}
